import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Hotspot } from '../types';

interface Props {
  hotspots: Hotspot[];
  onResize: (id: string, bounds: { x: number; y: number; width: number; height: number }) => void;
  onAdd: () => void;
  onEdit: (id: string, updates: { label?: string; description?: string }) => void;
  onDelete: (id: string) => void;
  isDirty: boolean;
  onSave: () => Promise<boolean>;
}

type Corner = 'tl' | 'tr' | 'bl' | 'br';

interface DragState {
  hotspotId: string;
  corner: Corner;
  fixedX: number;
  fixedY: number;
}

const HANDLE_SIZE = 12;

export const CustomizeMode: React.FC<Props> = ({
  hotspots, onResize, onAdd, onEdit, onDelete, isDirty, onSave,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editDesc, setEditDesc] = useState('');

  const getPercent = useCallback((clientX: number, clientY: number) => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100)),
    };
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent, hotspotId: string, corner: Corner) => {
    e.stopPropagation();
    e.preventDefault();
    const hotspot = hotspots.find(h => h.id === hotspotId);
    if (!hotspot) return;

    let fixedX: number, fixedY: number;
    switch (corner) {
      case 'tl': fixedX = hotspot.x + hotspot.width; fixedY = hotspot.y + hotspot.height; break;
      case 'tr': fixedX = hotspot.x; fixedY = hotspot.y + hotspot.height; break;
      case 'bl': fixedX = hotspot.x + hotspot.width; fixedY = hotspot.y; break;
      case 'br': fixedX = hotspot.x; fixedY = hotspot.y; break;
    }

    setDragState({ hotspotId, corner, fixedX, fixedY });
  }, [hotspots]);

  useEffect(() => {
    if (!dragState) return;

    const handleMouseMove = (e: MouseEvent) => {
      const { x: mouseX, y: mouseY } = getPercent(e.clientX, e.clientY);
      const { fixedX, fixedY, hotspotId } = dragState;
      const MIN_SIZE = 3;

      let newX = Math.min(mouseX, fixedX);
      let newY = Math.min(mouseY, fixedY);
      let newW = Math.abs(mouseX - fixedX);
      let newH = Math.abs(mouseY - fixedY);

      if (newW < MIN_SIZE) newW = MIN_SIZE;
      if (newH < MIN_SIZE) newH = MIN_SIZE;
      if (newX + newW > 100) newX = 100 - newW;
      if (newY + newH > 100) newY = 100 - newH;
      if (newX < 0) newX = 0;
      if (newY < 0) newY = 0;

      onResize(hotspotId, {
        x: Math.round(newX * 10) / 10,
        y: Math.round(newY * 10) / 10,
        width: Math.round(newW * 10) / 10,
        height: Math.round(newH * 10) / 10,
      });
    };

    const handleMouseUp = () => {
      setDragState(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, getPercent, onResize]);

  const openEdit = (hotspot: Hotspot) => {
    setEditingId(hotspot.id);
    setEditLabel(hotspot.label);
    setEditDesc(hotspot.description);
  };

  const saveEdit = () => {
    if (editingId) {
      onEdit(editingId, { label: editLabel, description: editDesc });
      setEditingId(null);
    }
  };

  const corners: { corner: Corner; getX: (h: Hotspot) => number; getY: (h: Hotspot) => number; cursor: string }[] = [
    { corner: 'tl', getX: h => h.x, getY: h => h.y, cursor: 'nw-resize' },
    { corner: 'tr', getX: h => h.x + h.width, getY: h => h.y, cursor: 'ne-resize' },
    { corner: 'bl', getX: h => h.x, getY: h => h.y + h.height, cursor: 'sw-resize' },
    { corner: 'br', getX: h => h.x + h.width, getY: h => h.y + h.height, cursor: 'se-resize' },
  ];

  return (
    <>
      {/* Overlay for coordinate calculation */}
      <div
        ref={overlayRef}
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 55,
        }}
      />

      {/* Resize handles for all hotspots */}
      {hotspots.map(h =>
        corners.map(({ corner, getX, getY, cursor }) => (
          <div
            key={`${h.id}-${corner}`}
            onMouseDown={(e) => handleMouseDown(e, h.id, corner)}
            style={{
              position: 'absolute',
              left: `calc(${getX(h)}% - ${HANDLE_SIZE / 2}px)`,
              top: `calc(${getY(h)}% - ${HANDLE_SIZE / 2}px)`,
              width: HANDLE_SIZE,
              height: HANDLE_SIZE,
              background: h.id.startsWith('custom_') ? '#4af' : '#fa4',
              border: '2px solid #fff',
              cursor,
              zIndex: 60,
              borderRadius: 2,
              pointerEvents: 'auto',
              boxSizing: 'border-box',
            }}
          />
        ))
      )}

      {/* Edit/Delete buttons for custom hotspots */}
      {hotspots.filter(h => h.id.startsWith('custom_')).map(h => (
        <div
          key={`ctrl-${h.id}`}
          style={{
            position: 'absolute',
            left: `${h.x}%`,
            top: `calc(${h.y}% - 30px)`,
            zIndex: 58,
            display: 'flex',
            gap: 4,
            pointerEvents: 'auto',
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); openEdit(h); }}
            style={controlBtnStyle}
            title="編集"
          >
            ✏️
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(h.id); }}
            style={{ ...controlBtnStyle, borderColor: '#f55' }}
            title="削除"
          >
            ✕
          </button>
        </div>
      ))}

      {/* Toolbar */}
      <div style={{
        position: 'absolute',
        top: 8,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 70,
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        pointerEvents: 'auto',
      }}>
        <span style={{
          color: '#4af',
          fontSize: '0.8rem',
          fontFamily: 'monospace',
          background: 'rgba(0,0,0,0.7)',
          padding: '4px 10px',
          borderRadius: 4,
          border: '1px solid #4af',
        }}>
          CUSTOMIZE
        </span>
        <button
          onClick={onAdd}
          style={{
            background: 'rgba(0,0,0,0.8)',
            border: '1px solid #4af',
            color: '#4af',
            padding: '6px 12px',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontFamily: '"Noto Serif JP", serif',
          }}
        >
          クリックエリア追加
        </button>
        <button
          onClick={onSave}
          disabled={!isDirty}
          style={{
            background: isDirty ? 'rgba(0,100,0,0.8)' : 'rgba(0,0,0,0.8)',
            border: `1px solid ${isDirty ? '#4f4' : '#555'}`,
            color: isDirty ? '#4f4' : '#555',
            padding: '6px 12px',
            borderRadius: 4,
            cursor: isDirty ? 'pointer' : 'default',
            fontSize: '0.85rem',
            fontFamily: '"Noto Serif JP", serif',
          }}
        >
          保存
        </button>
      </div>

      {/* Edit Modal */}
      {editingId && (
        <div
          onClick={() => setEditingId(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 200,
            pointerEvents: 'auto',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'linear-gradient(180deg, #1a1a1a 0%, #111 100%)',
              border: '1px solid #444',
              borderRadius: 8,
              padding: '1.5rem',
              width: 420,
              maxWidth: '90vw',
              fontFamily: '"Noto Serif JP", serif',
            }}
          >
            <h3 style={{
              color: '#c8a87c',
              fontSize: '1.2rem',
              marginBottom: '1.2rem',
              letterSpacing: '0.2em',
              borderBottom: '1px solid #333',
              paddingBottom: '0.5rem',
            }}>
              エリア編集
            </h3>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ color: '#888', fontSize: '0.9rem', display: 'block', marginBottom: 4 }}>ラベル</label>
              <input
                value={editLabel}
                onChange={e => setEditLabel(e.target.value)}
                style={inputStyle}
                autoFocus
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ color: '#888', fontSize: '0.9rem', display: 'block', marginBottom: 4 }}>クリック時のメッセージ</label>
              <textarea
                value={editDesc}
                onChange={e => setEditDesc(e.target.value)}
                rows={5}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditingId(null)} style={modalBtnStyle('#555')}>
                キャンセル
              </button>
              <button onClick={saveEdit} style={modalBtnStyle('#c8a87c')}>
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const controlBtnStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,0.85)',
  border: '1px solid #888',
  color: '#fff',
  width: 26,
  height: 26,
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: '0.75rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.6rem',
  background: '#0a0a0a',
  border: '1px solid #444',
  borderRadius: 4,
  color: '#eee',
  fontFamily: '"Noto Serif JP", serif',
  fontSize: '1rem',
  boxSizing: 'border-box',
  outline: 'none',
};

const modalBtnStyle = (color: string): React.CSSProperties => ({
  padding: '0.5rem 1.5rem',
  background: 'transparent',
  border: `1px solid ${color}`,
  color,
  borderRadius: 4,
  cursor: 'pointer',
  fontFamily: '"Noto Serif JP", serif',
  fontSize: '0.95rem',
  letterSpacing: '0.1em',
});
